/**
 * 来場者向け隠し称号サービス
 * 解除済みの称号だけを取得します。
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';

/** 解除済み隠し称号取得RPC */
const VISITOR_GET_HIDDEN_TITLES_RPC_NAME = 'visitor_get_hidden_titles';

/** 入力不正 */
export const VISITOR_HIDDEN_TITLES_INVALID_INPUT_CODE =
  'VISITOR_HIDDEN_TITLES_INVALID_INPUT';

/** セッション不正 */
export const VISITOR_HIDDEN_TITLES_SESSION_INVALID_CODE =
  'VISITOR_HIDDEN_TITLES_SESSION_INVALID';

/** 取得失敗 */
export const VISITOR_HIDDEN_TITLES_FETCH_FAILED_CODE =
  'VISITOR_HIDDEN_TITLES_FETCH_FAILED';

/** 解除済み隠し称号 */
export type VisitorHiddenTitle = {
  titleId: string;
  name: string;
  description: string;
  imageUrl: string | null;
  unlockedAt: string;
};

/**
 * RPCエラーを画面向けコードへ変換する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {string} アプリ向けエラーコード
 */
const mapHiddenTitleRpcErrorCode = (rpcError) => {
  const errorMessage = String(rpcError?.message ?? '');

  if (errorMessage.includes('INVALID_SESSION_TOKEN')) {
    return VISITOR_HIDDEN_TITLES_INVALID_INPUT_CODE;
  }

  if (
    errorMessage.includes('SESSION_NOT_FOUND')
    || errorMessage.includes('VISITOR_INACTIVE')
  ) {
    return VISITOR_HIDDEN_TITLES_SESSION_INVALID_CODE;
  }

  return VISITOR_HIDDEN_TITLES_FETCH_FAILED_CODE;
};

/**
 * 解除済み隠し称号を取得する
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<VisitorHiddenTitle[]>} 解除済み称号
 * @throws {AppError} 入力・通信エラー時
 */
export const visitorGetHiddenTitles = async (
  sessionToken,
): Promise<VisitorHiddenTitle[]> => {
  const sessionTokenValidation = validateRequiredString(
    sessionToken,
    'セッショントークン',
  );

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_HIDDEN_TITLES_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  const supabaseClient = getSupabaseClient();
  const { data, error } = await supabaseClient.rpc(
    VISITOR_GET_HIDDEN_TITLES_RPC_NAME,
    { p_session_token: sessionToken.trim() },
  );

  if (error) {
    const mappedErrorCode = mapHiddenTitleRpcErrorCode(error);

    logError('visitor_get_hidden_titles RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('隠し称号の取得に失敗しました。', {
      code: mappedErrorCode,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((titleRow) => ({
    titleId: String(titleRow?.title_id ?? ''),
    name: String(titleRow?.name ?? ''),
    description: String(titleRow?.description ?? ''),
    imageUrl: null,
    unlockedAt: String(titleRow?.unlocked_at ?? ''),
  }));
};
